/**
 * Board Store
 * Manages Kanban board state using Zustand
 */

import { create } from 'zustand';
import {
  BoardData,
  BoardIssue,
} from '@/types';

interface BoardState {
  // State
  boardData: BoardData | null;
  backlogIssues: BoardIssue[];
  isLoading: boolean;
  isMovingIssue: boolean;
  error: string | null;
  selectedIssue: BoardIssue | null;
  draggedIssue: BoardIssue | null;

  // Actions
  setBoardData: (data: BoardData | null) => void;
  setBacklogIssues: (issues: BoardIssue[]) => void;
  setIsLoading: (isLoading: boolean) => void;
  setIsMovingIssue: (isMoving: boolean) => void;
  setError: (error: string | null) => void;
  setSelectedIssue: (issue: BoardIssue | null) => void;
  setDraggedIssue: (issue: BoardIssue | null) => void;

  // Issue manipulation
  moveIssueToColumn: (
    taskId: string,
    targetStatusId: string,
    targetPosition: number
  ) => void;
  updateIssueInBoard: (issue: BoardIssue) => void;
  removeIssueFromBoard: (taskId: string) => void;
  addIssueToBacklog: (issue: BoardIssue) => void;
  removeIssueFromBacklog: (taskId: string) => void;

  // Column manipulation
  toggleColumnCollapse: (columnId: string) => void;

  // Clear
  clearBoard: () => void;
}

export const useBoardStore = create<BoardState>()((set, get) => ({
  // Initial state
  boardData: null,
  backlogIssues: [],
  isLoading: false,
  isMovingIssue: false,
  error: null,
  selectedIssue: null,
  draggedIssue: null,

  // Actions
  setBoardData: (data) => set({ boardData: data }),

  setBacklogIssues: (issues) => set({ backlogIssues: issues }),

  setIsLoading: (isLoading) => set({ isLoading }),

  setIsMovingIssue: (isMoving) => set({ isMovingIssue: isMoving }),

  setError: (error) => set({ error }),

  setSelectedIssue: (issue) => set({ selectedIssue: issue }),

  setDraggedIssue: (issue) => set({ draggedIssue: issue }),

  // Optimistically move issue between columns
  moveIssueToColumn: (taskId, targetStatusId, targetPosition) => {
    const { boardData } = get();
    if (!boardData) return;

    // Find the issue in current columns
    let movedIssue: BoardIssue | undefined;
    const updatedColumns = boardData.columns.map((column) => {
      // Check if issue is in this column
      const issueIndex = column.issues.findIndex((i) => i.taskId === taskId);
      if (issueIndex !== -1) {
        movedIssue = { ...column.issues[issueIndex] };
        // Remove from source column
        return {
          ...column,
          issues: column.issues.filter((i) => i.taskId !== taskId),
          issueCount: column.issueCount - 1,
          isOverWipLimit: column.wipLimit
            ? column.issueCount - 1 > column.wipLimit
            : false,
        };
      }
      return column;
    });

    if (!movedIssue) return;

    // Update the moved issue
    movedIssue.workflowStatusId = targetStatusId;
    movedIssue.boardColumnPosition = targetPosition;

    // Add to target column
    const finalColumns = updatedColumns.map((column) => {
      if (column.workflowStatusId === targetStatusId) {
        const newIssues = [...column.issues];
        newIssues.splice(targetPosition, 0, movedIssue!);
        // Update positions
        newIssues.forEach((issue, index) => {
          issue.boardColumnPosition = index;
        });
        return {
          ...column,
          issues: newIssues,
          issueCount: column.issueCount + 1,
          isOverWipLimit: column.wipLimit
            ? column.issueCount + 1 > column.wipLimit
            : false,
        };
      }
      return column;
    });

    set({
      boardData: {
        ...boardData,
        columns: finalColumns,
      },
    });
  },

  updateIssueInBoard: (issue) => {
    const { boardData } = get();
    if (!boardData) return;

    const updatedColumns = boardData.columns.map((column) => ({
      ...column,
      issues: column.issues.map((i) =>
        i.taskId === issue.taskId ? issue : i
      ),
    }));

    set({
      boardData: {
        ...boardData,
        columns: updatedColumns,
      },
    });
  },

  removeIssueFromBoard: (taskId) => {
    const { boardData } = get();
    if (!boardData) return;

    const updatedColumns = boardData.columns.map((column) => {
      const filtered = column.issues.filter((i) => i.taskId !== taskId);
      return {
        ...column,
        issues: filtered,
        issueCount: filtered.length,
        isOverWipLimit: column.wipLimit
          ? filtered.length > column.wipLimit
          : false,
      };
    });

    set({
      boardData: {
        ...boardData,
        columns: updatedColumns,
      },
    });
  },

  addIssueToBacklog: (issue) => {
    set((state) => ({
      backlogIssues: [...state.backlogIssues, issue],
    }));
  },

  removeIssueFromBacklog: (taskId) => {
    set((state) => ({
      backlogIssues: state.backlogIssues.filter((i) => i.taskId !== taskId),
    }));
  },

  toggleColumnCollapse: (columnId) => {
    const { boardData } = get();
    if (!boardData) return;

    const updatedColumns = boardData.columns.map((column) =>
      column.boardColumnId === columnId
        ? { ...column, isCollapsed: !column.isCollapsed }
        : column
    );

    set({
      boardData: {
        ...boardData,
        columns: updatedColumns,
      },
    });
  },

  clearBoard: () =>
    set({
      boardData: null,
      backlogIssues: [],
      selectedIssue: null,
      draggedIssue: null,
    }),
}));

// Selectors
export const useBoardData = () => useBoardStore((state) => state.boardData);
export const useBoardColumns = () =>
  useBoardStore((state) => state.boardData?.columns ?? []);
export const useBacklogIssues = () =>
  useBoardStore((state) => state.backlogIssues);
export const useBoardLoading = () => useBoardStore((state) => state.isLoading);
export const useBoardMovingIssue = () =>
  useBoardStore((state) => state.isMovingIssue);
export const useBoardError = () => useBoardStore((state) => state.error);
export const useSelectedIssue = () =>
  useBoardStore((state) => state.selectedIssue);
export const useDraggedIssue = () =>
  useBoardStore((state) => state.draggedIssue);
export const useBoardSprint = () =>
  useBoardStore((state) => state.boardData?.sprint);

export const useColumnById = (columnId: string | undefined) =>
  useBoardStore((state) =>
    columnId
      ? state.boardData?.columns.find((c) => c.boardColumnId === columnId)
      : undefined
  );

export const useIssuesByStatus = (statusId: string | undefined) =>
  useBoardStore((state) => {
    if (!statusId) return [];
    const column = state.boardData?.columns.find(
      (c) => c.workflowStatusId === statusId
    );
    return column?.issues ?? [];
  });

export default useBoardStore;
